/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useEffect, useState, useCallback, useRef} from 'react';
import {useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react';
import {useGeocoding} from './use-geocoding';
import {useBrowserGeoCountry} from './use-browser-geo-country';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useLocation} from 'react-router-dom';
import {removeSiteLocaleFromPath} from '@salesforce/retail-react-app/app/utils/url';
import {
  getGeoRedirectDataFromCookie,
  setGeoRedirectDataToCookie,
  isRegionModalDismissed,
  setRegionModalDismissed,
} from '@amblique/king-living/app/utils/utils';
import {
  buildRegionHomeUrl,
  getSiteConfigForDetectedCountry,
} from '@amblique/king-living/app/utils/geo-region';
import {resolveRegionSwitchTargetUrl} from '@amblique/king-living/app/utils/region-switch-url';

/**
 * True when detected ISO country matches the storefront site's country (handles UK vs GB).
 *
 * @param {string|null|undefined} detectedCode
 * @param {string|null|undefined} siteCountryCode
 * @returns {boolean}
 */
function detectedCountryMatchesSite(detectedCode, siteCountryCode) {
  const d = detectedCode?.toUpperCase();
  const s = siteCountryCode?.toUpperCase();

  if (!d || !s) return false;
  if (d === s) return true;
  if ((d === 'GB' && s === 'UK') || (d === 'UK' && s === 'GB')) return true;

  return false;
}

const MOCK_GEO_QUERY_PARAM = 'klMockGeoCountry';

/**
 * Development-only: fake detected country (no client geolocation call).
 * Priority: options.mockDetectedCountry → window.__KL_MOCK_GEO_COUNTRY__ → ?klMockGeoCountry=XX
 *
 * @param {string|undefined} mockFromProp - options.mockDetectedCountry (ISO alpha-2)
 * @param {string} search - location.search
 * @returns {string|null}
 */
export function resolveMockDetectedCountry(mockFromProp, search) {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const fromOpt = mockFromProp;

  if (typeof fromOpt === 'string' && /^[A-Za-z]{2}$/.test(fromOpt.trim())) {
    return fromOpt.trim().toUpperCase();
  }

  const fromWin = window.__KL_MOCK_GEO_COUNTRY__;

  if (typeof fromWin === 'string' && /^[A-Za-z]{2}$/.test(fromWin.trim())) {
    return fromWin.trim().toUpperCase();
  }

  try {
    const q = new URLSearchParams(search || '');
    const v = q.get(MOCK_GEO_QUERY_PARAM);

    if (v && /^[A-Za-z]{2}$/.test(v.trim())) {
      return v.trim().toUpperCase();
    }
  } catch {
    // ignore invalid search
  }

  return null;
}

/**
 * Geo region hint: client Google Geolocation (considerIp) + server reverse geocode, cookie cache, optional modal to switch site.
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true]
 * @param {boolean} [options.debug=false]
 * @param {string} [options.mockDetectedCountry] - Dev: optional ISO alpha-2; also use ?klMockGeoCountry=XX or window.__KL_MOCK_GEO_COUNTRY__
 * @returns {Object}
 */
export function useGeoRedirect(options = {}) {
  const {enabled = true, debug = false, mockDetectedCountry: mockDetectedCountryProp} = options;
  const {site} = useMultiSite();
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const {loading, error} = useGeocoding();
  const {resolveBrowserGeoCountry} = useBrowserGeoCountry();
  const location = useLocation();

  const [status, setStatus] = useState('idle');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  const geoCheckInitiatedRef = useRef(false);
  const isCheckingRef = useRef(false);

  const log = useCallback((...args) => {
    if (debug) {
      console.log('[GEO-REDIRECT]', ...args);
    }
  }, [debug]);

  const markAsChecked = useCallback(
    (countryCode) => {
      if (!site?.id) return;

      setGeoRedirectDataToCookie(site.id, countryCode);
    },
    [site?.id]
  );

  const isSearchEngineBot = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
      return false;
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const botPatterns = [
      'googlebot',
      'google-inspectiontool',
      'storebot-google',
      'bingbot',
      'msnbot',
      'bingpreview',
      'duckduckbot',
      'baiduspider',
      'yandexbot',
      'slurp',
      'sogou',
      'facebookexternalhit',
      'twitterbot',
      'linkedinbot',
      'whatsapp',
      'telegrambot',
      'ahrefsbot',
      'semrushbot',
      'dotbot',
      'rogerbot',
      'screaming frog',
      'applebot',
      'slackbot',
      'discordbot',
      'pinterestbot',
      'redditbot'
    ];

    return botPatterns.some((pattern) => userAgent.includes(pattern));
  }, []);

  const shouldSkipPath = useCallback(() => {
    const pathname = location.pathname;
    const bare = removeSiteLocaleFromPath(pathname) || pathname;
    const logicalPath = bare.startsWith('/') ? bare : `/${bare}`;

    const resetPasswordCallback = site.preferences?.resetPasswordCallback || '/reset-password-callback';
    const passwordlessLoginCallback = site.preferences?.passwordlessLoginCallback || '/passwordless-login-callback';
    const socialLoginCallback = site.preferences?.socialLoginCallback || '/social-callback';

    // OAuth/callback/system paths only — not /login or /registration (region modal allowed there).
    const skipPrefixes = [
      '/callback',
      resetPasswordCallback,
      passwordlessLoginCallback,
      socialLoginCallback,
      '/mobify/',
      '/static/',
      '/assets/',
      '/_next/',
      '/favicon.ico',
      '/robots.txt',
      '/sitemap.xml',
      '/worker.js',
      '/checkout'
    ];

    if (skipPrefixes.some((prefix) => logicalPath.startsWith(prefix))) {
      return true;
    }

    if (pathname.match(/^\/[^/]+\/[^/]+\/oauth2\/jwks$/)) {
      return true;
    }

    return false;
  }, [location.pathname, site.preferences]);

  const presentSuggestion = useCallback(
    (countryCode, bypassDismissCookie = false) => {
      if (!countryCode || !site?.id) {
        return false;
      }

      if (!bypassDismissCookie && isRegionModalDismissed(site.id)) {
        log('Region modal dismissed via cookie; skip suggestion');
        setStatus('no-redirect');

        return false;
      }

      if (detectedCountryMatchesSite(countryCode, site.countryCode)) {
        log('Detected country matches current site');
        markAsChecked(countryCode);
        setStatus('no-redirect');

        return false;
      }

      const cfg = getSiteConfigForDetectedCountry(countryCode);

      if (!cfg) {
        log('No SITE_CONFIG mapping for country:', countryCode);
        markAsChecked(countryCode);
        setStatus('no-redirect');

        return false;
      }

      if (cfg.siteId === site.id) {
        markAsChecked(countryCode);
        setStatus('no-redirect');

        return false;
      }

      const homeUrl = buildRegionHomeUrl(cfg);

      if (!homeUrl) {
        log('Could not build home URL for target site:', cfg.siteId);
        markAsChecked(countryCode);
        setStatus('no-redirect');

        return false;
      }

      setDetectedCountry(countryCode);
      setSuggestion({
        detectedCountryCode: countryCode,
        targetSiteId: cfg.siteId,
        targetLocale: cfg.defaultLocale,
        countryLabel: cfg.country,
        homeUrl,
      });
      setStatus('suggested');

      return true;
    },
    [site, markAsChecked, log]
  );

  const dismissRegionSuggestion = useCallback(() => {
    if (site?.id) {
      setRegionModalDismissed(site.id);
    }

    const cc = suggestion?.detectedCountryCode || detectedCountry;

    if (cc) {
      markAsChecked(cc);
    }

    setSuggestion(null);
    setStatus('no-redirect');
  }, [site?.id, suggestion?.detectedCountryCode, detectedCountry, markAsChecked]);

  const confirmRegionSwitch = useCallback(async () => {
    if (!suggestion?.homeUrl || !suggestion?.targetSiteId) return;

    markAsChecked(suggestion.detectedCountryCode);

    const url = await resolveRegionSwitchTargetUrl({
      siteConfigEntry: {siteId: suggestion.targetSiteId},
      pathname: location.pathname,
      search: location.search,
      shopperProducts: api.shopperProducts,
      getToken: getTokenWhenReady,
      currentSiteId: site?.id,
    });

    if (url) {
      window.location.href = url;
    }
  }, [
    api.shopperProducts,
    getTokenWhenReady,
    location.pathname,
    location.search,
    markAsChecked,
    site?.id,
    suggestion
  ]);

  /**
   * Run a fresh geo lookup and present suggestion if still mismatched.
   */
  const performGeoRedirect = useCallback(async () => {
    if (isCheckingRef.current) {
      log('Geo-redirect check already in progress, skipping...');

      return;
    }

    try {
      isCheckingRef.current = true;
      setStatus('checking');
      log('Starting geo-redirect via client Google Geolocation (considerIp)...');

      const apiKey = site.preferences?.GOOGLE_GEOLOCATION_BROWSER_API_KEY;
      let countryCode = null;

      if (!apiKey) {
        log('GOOGLE_GEOLOCATION_BROWSER_API_KEY not configured; treating as unknown region');
        setStatus('no-redirect');
        markAsChecked(null);

        return;
      }

      try {
        countryCode = await resolveBrowserGeoCountry(apiKey);
        log('Client geolocation countryCode:', countryCode);
      } catch (e) {
        log('Client geolocation failed:', e?.message || e);
      }

      if (!countryCode) {
        log('No country from client geolocation; treating as unknown region');
        setStatus('no-redirect');
        markAsChecked(null);

        return;
      }

      presentSuggestion(countryCode);
    } catch (err) {
      log('Error during geo-redirect:', err);
      setStatus('error');
      markAsChecked(null);
    } finally {
      isCheckingRef.current = false;
    }
  }, [
    resolveBrowserGeoCountry,
    site.preferences?.GOOGLE_GEOLOCATION_BROWSER_API_KEY,
    presentSuggestion,
    markAsChecked,
    log,
  ]);

  useEffect(() => {
    if (!enabled) {
      log('Geo-redirect disabled');

      return;
    }

    if (!site?.id) {
      return;
    }

    if (geoCheckInitiatedRef.current) {
      log('Geo-redirect check already initiated, skipping...');

      return;
    }

    if (isSearchEngineBot()) {
      log('Skipping geo-redirect for search engine bot');
      setStatus('no-redirect');

      return;
    }

    if (shouldSkipPath()) {
      log('Skipping geo-redirect for path:', location.pathname);
      setStatus('no-redirect');

      return;
    }

    const mockCountry = resolveMockDetectedCountry(mockDetectedCountryProp, location.search);

    if (mockCountry) {
      log('Using mock detected country (development only):', mockCountry);
      geoCheckInitiatedRef.current = true;
      setStatus('checking');
      presentSuggestion(mockCountry, true);

      return;
    }

    if (isRegionModalDismissed(site.id)) {
      log('Region modal dismiss cookie active; skip geo flow');
      setStatus('no-redirect');
      geoCheckInitiatedRef.current = true;

      return;
    }

    const cached = getGeoRedirectDataFromCookie(site.id);

    if (cached) {
      if (!cached.country) {
        log('Geo cookie present without country; skip until cookie expires');
        setStatus('no-redirect');
        geoCheckInitiatedRef.current = true;

        return;
      }

      log('Using cached country from cookie:', cached.country);

      if (detectedCountryMatchesSite(cached.country, site.countryCode)) {
        log('Cached country matches current site');
        setStatus('no-redirect');
        geoCheckInitiatedRef.current = true;

        return;
      }

      geoCheckInitiatedRef.current = true;
      // Cookie can be stale (e.g., VPN/travel). Revalidate once; only show modal if fresh detection still mismatches.
      performGeoRedirect();

      return;
    }

    log('No valid geo cookie, resolving country via client geolocation');
    geoCheckInitiatedRef.current = true;
    performGeoRedirect();
  }, [
    enabled,
    location.pathname,
    location.search,
    site?.id,
    mockDetectedCountryProp,
    log,
    isSearchEngineBot,
    shouldSkipPath,
    presentSuggestion,
    performGeoRedirect
  ]);

  return {
    status,
    loading,
    error,
    detectedCountry,
    suggestion,
    isRegionModalOpen: status === 'suggested' && Boolean(suggestion?.homeUrl),
    dismissRegionSuggestion,
    confirmRegionSwitch,
  };
}
